import { httpResource } from '@angular/common/http';
import { DatePipe, DecimalPipe } from '@angular/common';
import { Component, computed } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { LeafletGroupComponent } from '@components/leaflet/group/group.component';
import { environment } from '@environment';
import { ItemListResponse, Record, addProjection } from '@models';

/**
 * the index of the latest feature in the features signal.
 */
const LATEST = 1;

@Component({
  selector: 'app-kph',
  templateUrl: './kph.component.html',
  styleUrls: ['./kph.component.scss'],
  imports: [
    DatePipe,
    DecimalPipe,
    LeafletGroupComponent,
    MatCardModule,
    MatIconModule,
    MatListModule,
  ],
})
export class KPHLiveComponent {
  private readonly earthRadiusInNM = 3440.065;
  private readonly expirationInterval = 3_600_000; // one hour in ms
  private readonly speedThreshold = 0.001; // when SOG < speedThreshold, no COG is calculated

  datasetId = environment.kphLiveDatasetId;

  /**
   * gets (at least) two records from Kinko API and returns a list ofGeoJSON
   * Point Features.
   */
  private response = httpResource<GeoJSON.Feature<GeoJSON.Point>[]>(
    () => ({
      url: `${environment.kinkoHost}/dataset/${this.datasetId}/record/`,
      params: {
        take: 16, // 1-14 are ignored
        order: 'created:desc',
      },
    }),
    {
      parse: (raw: unknown) => {
        return (raw as ItemListResponse<Record>).items.map(
          (r) => r.content as unknown as GeoJSON.Feature<GeoJSON.Point>,
        );
      },
    },
  );

  /**
   * gets a pair of features. The first feature is an older feature, that is
   * used to calculate effective SOG and COG. The second feature is the latest
   * feature. Any of the features can be null and the features are based on
   * different records.
   */
  private features = computed(() => {
    const res = this.response;
    if (res.error() || !res.hasValue()) {
      return [null, null];
    }
    const ff = res.value();
    return [ff.length >= 2 ? ff[ff.length - 1] : null, ff.length ? ff[0] : null];
  });

  /**
   * gets a FeatureCollection for usage with Leaflet. The feature is of type
   * Point and has a projection property.
   */
  geometry = computed(() => {
    const f = this.features()[LATEST];
    return f
      ? ({
          type: 'FeatureCollection',
          features: [addProjection(f)],
        } as GeoJSON.FeatureCollection<GeoJSON.Point>)
      : null;
  });

  /**
   * gets the position as [longitude, latitude]
   */
  position = computed(() => this.features()[LATEST]?.geometry?.coordinates ?? null);

  /**
   * gets the UTC date from the latest feature
   */
  date = computed(() => this.getDate(this.features()[LATEST]));

  /**
   * returns true, when the latest data is older than one hour (See
   * expirationInterval).
   */
  isOutdated = computed(() => {
    const d = this.date();
    return d ? Date.now() - d.getTime() > this.expirationInterval : null;
  });

  /**
   * gets the speed over ground from the latest feature.
   */
  currentSOG = computed(() => this.features()[LATEST]?.properties?.['speed'] ?? null);

  /**
   * gets the time difference between the latest and the older feature.
   */
  timeDiff = computed(() => {
    const [f0, f1] = this.features();
    if (!f0 || !f1) {
      return null;
    }

    const d0 = this.getDate(f0);
    const d1 = this.getDate(f1);
    return d0 && d1 ? (d1.getTime() - d0.getTime()) / 1000 : null;
  });

  /**
   * calculates the speed over ground (SOG) from older and latest feature using
   * the Haversine formula for calculating the distance.
   */
  effectiveSOG = computed(() => {
    const timeDiff = this.timeDiff();
    if (!timeDiff) {
      return null;
    }
    const [f0, f1] = this.features();
    if (!f0 || !f1) {
      return null;
    }

    const [long1, lat1] = f0.geometry.coordinates.map(this.toRad);
    const [long2, lat2] = f1.geometry.coordinates.map(this.toRad);
    const dlat = lat2 - lat1;
    const dlong = long2 - long1;
    const a = Math.sin(dlat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dlong / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = this.earthRadiusInNM * c;

    return (distance / timeDiff) * 3_600; // speed in knots
  });

  /**
   * calculates the initial bearing (or forward azimuth) from older and latest
   * feature. When the speed is near zero, COG is meaningless and not calculated.
   */
  effectiveCOG = computed(() => {
    const sog = this.effectiveSOG();
    if (sog && sog < this.speedThreshold) {
      return null;
    }
    const [f0, f1] = this.features();
    if (!f0 || !f1) {
      return null;
    }

    const [long1, lat1] = f0.geometry.coordinates.map(this.toRad);
    const [long2, lat2] = f1.geometry.coordinates.map(this.toRad);
    const dlong = long2 - long1;
    const y = Math.sin(dlong) * Math.cos(lat2);
    const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dlong);
    return (this.toDeg(Math.atan2(y, x)) + 360) % 360;
  });

  /**
   * get the date from the properties of a feature.
   */
  private getDate(
    f: GeoJSON.Feature<GeoJSON.Point, GeoJSON.GeoJsonProperties> | null,
  ): Date | null {
    if (!f) {
      return null;
    }
    const ts = f.properties?.['datetime_utc'];
    return ts ? new Date(ts) : null;
  }

  /**
   * convert degrees to radian
   */
  private toRad(deg: number): number {
    return (deg * Math.PI) / 180;
  }

  /**
   * convert radian to degrees
   */
  private toDeg(rad: number): number {
    return (rad * 180) / Math.PI;
  }
}
